/**
 * Log Event Model
 *
 * Defines the structure for log events emitted by the fga-manager web component.
 * These events allow the host application to capture and handle logging from
 * within the component, enabling integration with external logging systems.
 *
 * @packageDocumentation
 */

/**
 * Log level severity enum.
 * Levels are ordered from least to most severe.
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Numeric priority for log levels (higher = more severe).
 * Used for filtering logs based on minimum severity.
 */
export const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Log event structure emitted by the web component.
 *
 * @example
 * ```typescript
 * const event: LogEvent = {
 *   level: 'error',
 *   message: 'Failed to load groups',
 *   source: 'FgaApiService',
 *   timestamp: Date.now(),
 *   data: { groupId: 'group:org|team', error: 'Network error' }
 * };
 * ```
 */
export interface LogEvent {
  /** Severity level of the log entry */
  level: LogLevel;

  /** Human-readable log message */
  message: string;

  /** Source component or service name */
  source: string;

  /** Unix timestamp in milliseconds when the event occurred */
  timestamp: number;

  /** Optional contextual data associated with the log entry */
  data?: unknown;
}
